import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Parse from 'parse';

const DeleteOperatorGameConfigDialog = ({ open, onClose, onDelete, config }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!config?.id) return;

    setLoading(true);
    setError('');
    try {
      await Parse.Cloud.run('rgsDeleteOperatorGameConfig', { configId: config.id });
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting config:', err);
      setError(err.message || 'Failed to delete config');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#242424', m: 0 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#242424',
          color: '#F44336',
          pb: 1
        }}
      >
        <WarningAmberIcon />
        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Delete Game Config</Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#242424', pt: 1, pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
            Are you sure you want to delete the RTP config for:
          </Typography>

          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              borderRadius: '6px',
              border: '1px solid rgba(244, 67, 54, 0.2)'
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 500 }}>{config?.operatorName}</Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
              Game: {config?.gameSlug} | Mode: {config?.modeName || '-'} | RTP:{' '}
              {config?.rtpLevel != null ? `${config.rtpLevel}%` : '-'}
            </Typography>
          </Box>

          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
            This operator will fall back to the default (highest) RTP level for this game.
          </Typography>

          {error && (
            <Typography sx={{ color: '#F44336', fontSize: '13px', textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              fullWidth
              disabled={loading}
              sx={{
                flex: 1,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                borderRadius: '4px',
                py: 1,
                textTransform: 'capitalize',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                flex: 1,
                backgroundColor: '#F44336',
                color: '#fff',
                borderRadius: '4px',
                py: 1,
                fontWeight: 600,
                textTransform: 'capitalize',
                '&:hover': { backgroundColor: '#D32F2F' }
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteOperatorGameConfigDialog;
